import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useSEO } from "@/hooks/useSEO";
import { ArrowRight, Linkedin, Mail } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamPage() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  useSEO("Our Team | Equaliser Legal LLP", "Meet the team at Equaliser Legal LLP — experienced lawyers across corporate, commercial, contracts, compliance, and dispute resolution.");

  useEffect(() => {
    axios.get(`${API}/team`).then(res => { setTeam(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const founding = team.filter(m => m.category === "founding");
  const associates = team.filter(m => m.category === "associate");

  return (
    <div data-testid="team-page">

      <section className="bg-[#0A1628] noise-overlay relative pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">Our Team</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-medium leading-tight tracking-tight max-w-3xl">Experience across business and law.</h1>
          <p className="text-white/50 text-base md:text-lg leading-relaxed max-w-2xl mt-6">Equaliser Legal LLP brings together a founding partner with senior corporate leadership experience and a team of qualified lawyers through our associated law firm.</p>
        </div>
      </section>

      {founding.length > 0 && (
        <section data-testid="founding-partner-section" className="bg-white py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-12">Founding Partner</p>
            {founding.map(member => (
              <div key={member.id} className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
                <div className="lg:col-span-1">
                  <div className="aspect-[3/4] overflow-hidden rounded-[2px] bg-slate-100">
                    {member.photo_url ? <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300 text-6xl font-heading">{member.name.charAt(0)}</div>}
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-2">{member.name}</h2>
                  <p className="text-[#C5A059] text-sm uppercase tracking-wider mb-1">{member.role}</p>
                  {member.location && <p className="text-slate-400 text-xs mb-6">{member.location}</p>}
                  <p className="text-slate-600 text-base leading-relaxed mb-6">{member.bio}</p>
                  <div className="space-y-3">
                    <div><span className="text-xs uppercase tracking-wider text-slate-400">Education</span><p className="text-slate-600 text-sm mt-1">{member.education}</p></div>
                    {member.bar_enrolment && <div><span className="text-xs uppercase tracking-wider text-slate-400">Bar Enrolment</span><p className="text-slate-600 text-sm mt-1">{member.bar_enrolment}</p></div>}
                    <div>
                      <span className="text-xs uppercase tracking-wider text-slate-400">Practice Areas</span>
                      <div className="flex flex-wrap gap-2 mt-2">{member.practice_areas.map((a, j) => <span key={j} className="text-xs border border-slate-200 text-slate-500 px-3 py-1 rounded-[2px]">{a}</span>)}</div>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    {member.linkedin_url && <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#C5A059] transition-colors"><Linkedin size={18} /></a>}
                    {member.contact_email && <a href={`mailto:${member.contact_email}`} className="text-slate-400 hover:text-[#C5A059] transition-colors"><Mail size={18} /></a>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {associates.length > 0 && (
        <section data-testid="associate-lawyers-section" className="bg-[#F8FAFC] py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">Our Team And Affiliates Network</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-4">Our Equaliser Legal Team.</h2>
            <p className="text-slate-500 text-base mb-16 max-w-2xl">Equaliser Legal LLP operates with an integrated team of highly qualified lawyers, enabling us to deliver seamless, high-quality legal advisory across matters and jurisdictions.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {associates.map(member => (
                <div key={member.id} data-testid={`team-member-${member.id}`} className="bg-white border border-slate-200 hover:border-[#0A1628] transition-colors duration-300 group overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    {member.photo_url ? <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover object-top" /> : <div className="w-full h-full flex items-center justify-center text-slate-300 text-4xl font-heading">{member.name.charAt(0)}</div>}
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-lg text-[#0A1628] font-medium">{member.name}</h3>
                    <p className="text-[#C5A059] text-xs uppercase tracking-wider mt-1 mb-1">{member.role}</p>
                    {member.location && <p className="text-slate-400 text-[10px] mb-4">{member.location}</p>}

                    {/* Bio with HoverCard popup */}
                    <HoverCard openDelay={200} closeDelay={100}>
                      <HoverCardTrigger asChild>
                        <p
                          data-testid={`team-bio-trigger-${member.id}`}
                          className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-3 cursor-pointer hover:text-slate-700 transition-colors border-b border-dashed border-slate-300 pb-1"
                        >
                          {member.bio}
                        </p>
                      </HoverCardTrigger>
                      <HoverCardContent
                        data-testid={`team-bio-popup-${member.id}`}
                        side="top"
                        align="start"
                        className="w-80 max-h-64 overflow-y-auto"
                      >
                        <div className="space-y-2">
                          <p className="font-heading text-sm font-medium text-[#0A1628]">{member.name}</p>
                          <p className="text-[#C5A059] text-[10px] uppercase tracking-wider">{member.role}</p>
                          <p className="text-slate-600 text-sm leading-relaxed">{member.bio}</p>
                          {member.practice_areas && member.practice_areas.length > 0 && (
                            <div className="pt-2 border-t border-slate-100">
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Practice Areas</p>
                              <div className="flex flex-wrap gap-1">
                                {member.practice_areas.map((a, j) => (
                                  <span key={j} className="text-[10px] border border-slate-200 text-slate-400 px-1.5 py-0.5 rounded-[2px]">{a}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </HoverCardContent>
                    </HoverCard>

                    <p className="text-slate-400 text-xs">{member.education}</p>
                    {member.bar_enrolment && <p className="text-slate-400 text-xs">{member.bar_enrolment}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-4">{member.practice_areas.slice(0, 3).map((a, j) => <span key={j} className="text-[10px] border border-slate-200 text-slate-400 px-2 py-0.5 rounded-[2px]">{a}</span>)}</div>
                    <div className="flex gap-2 mt-4">
                      {member.linkedin_url && <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-[#C5A059]"><Linkedin size={14} /></a>}
                      {member.contact_email && <a href={`mailto:${member.contact_email}`} className="text-slate-300 hover:text-[#C5A059]"><Mail size={14} /></a>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {loading && <section className="bg-white py-24"><div className="max-w-7xl mx-auto px-6 md:px-12 text-center"><p className="text-slate-400">Loading team information...</p></div></section>}

      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-6">Work with our team.</h2>
          <Link to="/contact" data-testid="team-cta" className="inline-flex items-center justify-center bg-[#0A1628] text-white px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#1a2c4e] transition-colors duration-300 rounded-[2px]">Get in touch <ArrowRight size={16} className="ml-2" /></Link>
        </div>
      </section>
    </div>
  );
}
